import { useEffect, useMemo, useState } from 'react'

import { FetchVideoViewsResultSort } from '@api'
import { subDays } from 'date-fns'
import { format } from 'date-fns/format'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'

import SearchIcon from '@/assets/icons/icon-search.svg'
import { Page } from '@/components/layout/page/page.tsx'
import {
  DateRangeContextKey,
  useDateRangeContext,
} from '@/components/provider/date-range-provider.tsx'
import { Button } from '@/components/ui/button.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { Export, getFormattedDateRange } from '@/components/ui/export'
import { SortOptionsSelect } from '@/components/ui/table/components/sort-options-select.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { WithRefresher } from '@/components/ui/with-refresher.tsx'
import { $creatorId } from '@/models/creators.ts'
import { $hasModifySocialAccountsPermission } from '@/models/permissions.ts'
import {
  $socialViewSortOptions,
  setSortOptionSocial,
} from '@/modules/statistics/social/data/models.ts'
import { useSocialData } from '@/modules/statistics/social/hooks/use-social-data.ts'
import { SocialMediaTypeProvider } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import {
  SocialMediaType,
  socialMessages,
} from '@/modules/statistics/social/types.ts'
import { exportDataFromServer } from '@/utils/export/export'
import {
  TrackedFeatures,
  trackFeatureUsage,
} from '@/utils/feature-engagement.ts'

import { useExportSocial } from '../hooks/use-export.tsx'
import { SocialPageView } from './SocialPageView.tsx'

interface SocialViewProps {
  socialMediaType: SocialMediaType
}

export const SocialView = ({ socialMediaType }: SocialViewProps) => {
  const queryClient = useQueryClient()
  const { date, setDate, maxDateRange, today } = useDateRangeContext(
    DateRangeContextKey.Social,
  )
  const [query, setQuery] = useState('')
  const hasModifySocialAccountsPermission = useUnit(
    $hasModifySocialAccountsPermission,
  )
  const creatorId = useUnit($creatorId)
  const sortOption = useUnit($socialViewSortOptions)

  const sortOptions = useMemo(() => {
    return {
      Views: FetchVideoViewsResultSort.VIEWS,
      Likes: FetchVideoViewsResultSort.LIKES,
      Comments: FetchVideoViewsResultSort.COMMENTS,
      Shares: FetchVideoViewsResultSort.SHARES,
    }
  }, [])

  const socialData = useSocialData({
    dateRange: date,
    socialMediaType,
  })

  const { filledAccountsMap } = useExportSocial()

  const socialDateRange = useMemo(() => {
    const yesterday = subDays(today, 1)

    return {
      from: maxDateRange?.from ?? new Date(0),
      to: yesterday,
    }
  }, [maxDateRange, today])

  const refetchAll = () => {
    socialData.refetch()

    if (creatorId && socialData.accounts) {
      for (const account of socialData.accounts.views) {
        queryClient.refetchQueries([
          creatorId,
          socialMediaType,
          account.account,
          socialDateRange,
        ])
      }
    }
  }

  const accountsToExport = useMemo(() => {
    const accounts = socialData.accounts?.views ?? []

    if (!accounts.length) return []

    const isAllAccountsFetched = accounts.every(
      (account) => typeof filledAccountsMap[account.account] === 'boolean',
    )

    return isAllAccountsFetched
      ? accounts
          .filter((account) => filledAccountsMap[account.account])
          .map(({ account }) => account)
      : []
  }, [socialData.accounts, filledAccountsMap])

  const downloadSheet = () => {
    if (!creatorId || !date.from || !date.to || !accountsToExport.length)
      return Promise.resolve()

    const filename = `${socialMediaType} Statistics, ${getFormattedDateRange(
      date,
    )}`
    const commonFields = {
      type: socialMediaType,
      from: format(date.from, 'yyyy-MM-dd'),
      to: format(date.to, 'yyyy-MM-dd'),
    }

    if (accountsToExport.length === 1) {
      return exportDataFromServer(creatorId, {
        type: 'Social',
        filename,
        fields: {
          ...commonFields,
          accountName: accountsToExport[0],
        },
      })
    } else {
      return exportDataFromServer(creatorId, {
        type: 'MultiSocial',
        filename,
        fields: {
          ...commonFields,
          accountNames: accountsToExport.join(','),
        },
      })
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  useEffect(() => {
    if (socialData.accounts?.views.length) {
      trackFeatureUsage(
        socialMediaType === SocialMediaType.Instagram
          ? TrackedFeatures.InstagramStatistics
          : TrackedFeatures.TiktokStatistics,
        creatorId,
      )
    }
  }, [socialData, socialMediaType, creatorId])

  return (
    <Page
      title={`${socialMessages[socialMediaType].socialDisplayName} Statistics`}
      headerOptions={
        <>
          <SortOptionsSelect
            sortOptions={sortOptions}
            sortOption={sortOption}
            handleSortChange={setSortOptionSocial}
            className="max-w-[30px]"
          />
          <TextField
            placeholder="Search"
            className="sm:max-w-[200px] min-w-[80px] flex-1"
            prefix={
              <SearchIcon className="text-title dark:text-secondary-300" />
            }
            onChange={handleSearch}
            value={query}
          />
          <Export
            getFromServer={downloadSheet}
            disabled={!accountsToExport.length}
          />
          <WithRefresher
            onRefresh={refetchAll}
            isFetching={socialData.isFetching}
          >
            <CalendarRangeDatePicker
              date={date}
              setDate={setDate}
              maxDateRange={socialDateRange}
            />
          </WithRefresher>
          {hasModifySocialAccountsPermission ? (
            <Link
              to="accounts"
              className="truncate"
            >
              <Button>Manage Accounts</Button>
            </Link>
          ) : (
            <Tooltip content="You don't have the permission to manage the accounts.">
              <Button
                as="div"
                disabled
              >
                Manage Accounts
              </Button>
            </Tooltip>
          )}
        </>
      }
    >
      <SocialMediaTypeProvider socialMediaType={socialMediaType}>
        <SocialPageView
          data={socialData}
          query={query}
        />
      </SocialMediaTypeProvider>
    </Page>
  )
}
